import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Package, Clock, Truck, CheckCircle, XCircle, MapPin, Navigation, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';

const Orders = ({ user, onLogout }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get('/orders');
      setOrders(response.data);
    } catch (error) {
      toast.error('Error al cargar pedidos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('¿Estás seguro de cancelar este pedido?')) return;

    try {
      await axios.post(`/orders/${orderId}/cancel`);
      toast.success('Pedido cancelado');
      fetchOrders();
    } catch (error) {
      toast.error('Error al cancelar pedido');
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      'CREATED': { label: 'Creado', icon: Package, className: 'status-badge status-created' },
      'READY_FOR_PICKUP': { label: 'Listo para Recoger', icon: Clock, className: 'status-badge status-ready' },
      'IN_CUSTODY': { label: 'En Custodia', icon: Truck, className: 'status-badge status-in-custody' },
      'EN_ROUTE': { label: 'En Ruta', icon: Truck, className: 'status-badge status-en-route' },
      'ARRIVED': { label: 'Llegado', icon: MapPin, className: 'status-badge status-ready' },
      'DELIVERED': { label: 'Entregado', icon: CheckCircle, className: 'status-badge status-delivered' },
      'FAILED_DELIVERY': { label: 'Fallo en Entrega', icon: XCircle, className: 'status-badge status-failed' },
      'CANCELLED': { label: 'Cancelado', icon: XCircle, className: 'status-badge status-cancelled' }
    };
    return statusMap[status] || statusMap['CREATED'];
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.customer.name.toLowerCase().includes(filter.toLowerCase()) ||
                          order.id.toLowerCase().includes(filter.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <Layout user={user} onLogout={onLogout} title="Pedidos">
      <div className="space-y-6" data-testid="orders-container">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#224049' }}>Pedidos</h1>
            <p className="text-sm" style={{ color: '#666' }}>Gestiona y rastrea todos los pedidos</p>
          </div>
          {(user.role === 'admin' || user.role === 'dispensary_owner') && (
            <Button
              onClick={() => window.location.href = '/orders/create'}
              style={{ backgroundColor: '#224049', color: '#D4AD6A' }}
              data-testid="create-order-button"
            >
              Crear Pedido
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                placeholder="Buscar por nombre o ID..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                data-testid="search-orders-input"
              />
              <select
                className="px-3 py-2 border rounded-md"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="filter-status-select"
              >
                <option value="all">Todos los Estados</option>
                <option value="CREATED">Creado</option>
                <option value="READY_FOR_PICKUP">Listo para Recoger</option>
                <option value="IN_CUSTODY">En Custodia</option>
                <option value="EN_ROUTE">En Ruta</option>
                <option value="DELIVERED">Entregado</option>
                <option value="CANCELLED">Cancelado</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Orders List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-lg" style={{ color: '#224049' }}>Cargando pedidos...</div>
          </div>
        ) : filteredOrders.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="w-16 h-16 mx-auto mb-4" style={{ color: '#D9A27F' }} />
              <p className="text-lg" style={{ color: '#666' }}>No se encontraron pedidos</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order) => {
              const statusInfo = getStatusInfo(order.status);
              const StatusIcon = statusInfo.icon;
              
              return (
                <Card key={order.id} className="card fade-in shadow-lg hover:shadow-xl" data-testid={`order-card-${order.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                      <div className="flex items-center gap-3">
                        <StatusIcon className="w-6 h-6" style={{ color: statusInfo.className.includes('delivered') ? '#388E3C' : '#D9A27F' }} />
                        <div>
                          <CardTitle className="text-lg" style={{ color: '#224049' }}>
                            {order.customer.name}
                          </CardTitle>
                          <p className="text-sm" style={{ color: '#666' }}>ID: {order.id.substring(0, 8)}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <span className={order.mode === 'EXPRESS' ? 'mode-express' : 'mode-standard'}>
                          {order.mode === 'EXPRESS' ? 'EXPRESS 60\'' : 'STANDARD'}
                        </span>
                        <span className={statusInfo.className}>
                          {statusInfo.label}
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-xs" style={{ color: '#666' }}>Dirección</p>
                        <p className="font-medium text-sm" style={{ color: '#224049' }}>
                          {order.delivery_address.line1}, {order.delivery_address.city}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs" style={{ color: '#666' }}>Teléfono</p>
                        <p className="font-medium text-sm" style={{ color: '#224049' }}>{order.customer.phone}</p>
                      </div>
                      <div>
                        <p className="text-xs" style={{ color: '#666' }}>Tarifa</p>
                        <p className="font-bold text-sm" style={{ color: '#D9A27F' }}>${order.delivery_fee.toFixed(2)}</p>
                      </div>
                    </div>
                    
                    {order.items && order.items.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs mb-2" style={{ color: '#666' }}>Artículos</p>
                        <div className="space-y-1">
                          {order.items.map((item, idx) => (
                            <p key={idx} className="text-sm" style={{ color: '#224049' }}>
                              {item.quantity}x {item.name} ({item.sku})
                            </p>
                          ))}
                        </div>
                      </div>
                    )}

                    {order.pin && (
                      <div className="mb-4">
                        <p className="text-xs" style={{ color: '#666' }}>PIN de Entrega</p>
                        <p className="text-2xl font-bold tracking-wider" style={{ color: '#D4AD6A' }}>{order.pin}</p>
                      </div>
                    )}

                    {order.refund_eligible && (
                      <div className="p-3 rounded-lg mb-4" style={{ backgroundColor: '#FFF3E0', border: '1px solid #F57C00' }}>
                        <p className="text-sm font-semibold" style={{ color: '#F57C00' }}>
                          ⚠️ Elegible para reembolso (SLA Express incumplido)
                        </p>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.location.href = `/orders/${order.id}`}
                        data-testid={`track-order-button-${order.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver Detalles
                      </Button>
                      {(order.status === 'EN_ROUTE' || order.status === 'IN_CUSTODY') && (
                        <Button
                          size="sm"
                          className="bg-[#0F7A5B] hover:bg-[#0d6549] text-white"
                          asChild
                        >
                          <Link to={`/tracking/${order.id}`}>
                            <Navigation className="w-4 h-4 mr-1" />
                            Tracking
                          </Link>
                        </Button>
                      )}
                      {order.status !== 'DELIVERED' && order.status !== 'CANCELLED' && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleCancelOrder(order.id)}
                          data-testid={`cancel-order-button-${order.id}`}
                        >
                          Cancelar
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Orders;